import { Component, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { switchMap } from 'rxjs/operators';
import { DicteeFormulaire, DicteeService } from 'src/app/core/services/entities/dictee.service';

type Etape = 'attente' | 'enregistrement' | 'relecture' | 'envoi';

/**
 * Dictée d'un formulaire. Règle produit : rien n'est enregistré à l'ouverture ; l'utilisateur démarre,
 * arrête, réécoute, puis recommence ou envoie. Rien ne part au serveur avant « Envoyer ». En cas d'erreur
 * (quota, service indisponible), l'enregistrement est gardé pour réessayer.
 */
@Component({
  selector: 'bay-dictee',
  template: `
    <div class="rounded-4 p-3 mb-3" style="background:#f1f5eb;" data-etat="dictee" role="region" aria-label="Dictée">
      <ng-container *ngIf="etape === 'attente'">
        <button type="button" class="btn fw-bold d-inline-flex align-items-center gap-2" data-test="parler"
                style="background:#0d631b;color:#fff;border-radius:999px;padding:10px 18px;" (click)="demarrer()">
          <span class="material-symbols-outlined" aria-hidden="true">mic</span>Appuyer pour parler
        </button>
        <button type="button" class="btn btn-sm btn-light ms-2" (click)="annuler()">Annuler</button>
        <p class="mb-0 mt-2 text-muted" style="font-size:13px;">Rien n'est enregistré avant que vous appuyiez. {{ consigne }}</p>
      </ng-container>

      <div *ngIf="etape === 'enregistrement'" class="d-flex flex-wrap align-items-center gap-2">
        <span class="d-inline-flex align-items-center gap-2 fw-bold" style="color:#b91c1c;" aria-live="polite">
          <span style="width:10px;height:10px;border-radius:50%;background:#dc2626;display:inline-block;"></span>
          Enregistrement {{ chrono(duree) }} / {{ chrono(maxSecondes) }}
        </span>
        <button type="button" class="btn btn-sm btn-success" data-test="arreter" (click)="arreter()">Arrêter</button>
        <button type="button" class="btn btn-sm btn-light" (click)="annuler()">Annuler</button>
      </div>

      <div *ngIf="etape === 'relecture' || etape === 'envoi'">
        <audio *ngIf="url" [src]="url" controls class="w-100 mb-2" style="max-width:420px;" aria-label="Réécouter l'enregistrement"></audio>
        <div class="d-flex flex-wrap gap-2">
          <button type="button" class="btn btn-sm btn-light" [disabled]="etape === 'envoi'" (click)="recommencer()">Recommencer</button>
          <button type="button" class="btn btn-sm btn-success" data-test="envoyer" [disabled]="etape === 'envoi'" (click)="envoyer()">
            <span *ngIf="etape === 'envoi'" class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Envoyer
          </button>
          <button type="button" class="btn btn-sm btn-light" [disabled]="etape === 'envoi'" (click)="annuler()">Annuler</button>
        </div>
      </div>

      <div *ngIf="erreur" class="alert alert-warning py-2 mt-2 mb-0" role="alert" style="font-size:13px;">{{ erreur }}</div>
    </div>
  `
})
export class DicteeComponent implements OnDestroy {
  /** Formulaire dicté (ex. ARTICLE_STOCK) */
  @Input() formulaire = '';
  @Input() langue: 'fr' | 'en' = 'fr';
  @Input() consigne = '';
  @Input() maxSecondes = 60;
  @Output() resultat = new EventEmitter<DicteeFormulaire>();
  @Output() ferme = new EventEmitter<void>();

  etape: Etape = 'attente';
  duree = 0;
  url: string | null = null;
  erreur: string | null = null;

  private flux?: MediaStream;
  private enregistreur?: MediaRecorder;
  private morceaux: BlobPart[] = [];
  private audio?: Blob;
  private minuteur?: any;
  private debut = 0;

  constructor(private service: DicteeService, private zone: NgZone) {}

  chrono(s: number): string {
    const t = Math.floor(s);
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
  }

  async demarrer(): Promise<void> {
    this.erreur = null;
    if (!navigator.mediaDevices?.getUserMedia || typeof (window as any).MediaRecorder === 'undefined') {
      this.erreur = 'Ce navigateur ne permet pas d\'enregistrer le son. Essayez avec Chrome, Edge ou Firefox récent.';
      return;
    }
    try {
      this.flux = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e: any) {
      this.erreur = e?.name === 'NotAllowedError' || e?.name === 'SecurityError'
        ? 'Le micro est bloqué : autorisez-le pour ce site (icône à gauche de l\'adresse dans le navigateur), puis appuyez de nouveau.'
        : 'Aucun micro disponible : branchez-en un ou vérifiez les réglages, puis réessayez.';
      return;
    }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
      .find(t => (window as any).MediaRecorder.isTypeSupported?.(t));
    this.morceaux = [];
    this.enregistreur = type ? new MediaRecorder(this.flux, { mimeType: type }) : new MediaRecorder(this.flux);
    this.enregistreur.ondataavailable = ev => { if (ev.data?.size) { this.morceaux.push(ev.data); } };
    this.enregistreur.onstop = () => this.zone.run(() => this.apresArret());
    this.enregistreur.start();
    this.etape = 'enregistrement';
    this.duree = 0;
    this.debut = Date.now();
    this.minuteur = setInterval(() => this.zone.run(() => {
      this.duree = (Date.now() - this.debut) / 1000;
      // 60 s : arrêt automatique et relecture, sans rien envoyer
      if (this.duree >= this.maxSecondes) { this.arreter(); }
    }), 250);
  }

  arreter(): void {
    clearInterval(this.minuteur);
    if (this.enregistreur?.state === 'recording') { this.enregistreur.stop(); }
    this.couperMicro();
  }

  private apresArret(): void {
    if (this.etape !== 'enregistrement') { return; }   // annulé pendant l'enregistrement
    this.audio = new Blob(this.morceaux, { type: this.enregistreur?.mimeType || 'audio/webm' });
    this.liberer();
    this.url = URL.createObjectURL(this.audio);
    this.etape = 'relecture';
  }

  recommencer(): void {
    this.toutJeter();
    this.etape = 'attente';
  }

  annuler(): void {
    this.etape = 'attente';
    clearInterval(this.minuteur);
    if (this.enregistreur?.state === 'recording') { this.enregistreur.stop(); }
    this.couperMicro();
    this.toutJeter();
    this.ferme.emit();
  }

  /** Seul moment où quelque chose part au serveur */
  envoyer(): void {
    if (!this.audio) { return; }
    this.etape = 'envoi';
    this.erreur = null;
    this.service.transcrire(this.audio, this.langue).pipe(
      switchMap(t => {
        const texte = (t.traduction || t.transcription || '').trim();
        if (!texte) { throw { error: { message: 'Rien n\'a été compris : réécoutez puis recommencez en parlant plus près du micro.' } }; }
        return this.service.formulaire(this.formulaire, texte, this.langue);
      })
    ).subscribe({
      next: r => { this.resultat.emit(r); this.toutJeter(); this.etape = 'attente'; },
      error: e => {
        // Quota (429), service indisponible (502/503)… : message du serveur, l'enregistrement est gardé
        this.erreur = e?.error?.message || (e?.status === 429 ? 'Quota de dictée atteint pour aujourd\'hui.' : 'La dictée n\'a pas pu être traitée. Réessayez dans un instant.');
        this.etape = 'relecture';
      }
    });
  }

  private couperMicro(): void {
    this.flux?.getTracks().forEach(t => t.stop());
    this.flux = undefined;
  }

  private liberer(): void {
    if (this.url) { URL.revokeObjectURL(this.url); this.url = null; }
  }

  private toutJeter(): void {
    this.liberer();
    this.audio = undefined;
    this.morceaux = [];
    this.erreur = null;
  }

  ngOnDestroy(): void {
    clearInterval(this.minuteur);
    if (this.enregistreur?.state === 'recording') { this.enregistreur.stop(); }
    this.couperMicro();
    this.liberer();
  }
}
